import { TlfArsListingReadDto, TlfArsTableReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { cloneDeep } from "lodash";
import { applyPatch, Patch } from "rfc6902";
import { findVariantPatchConflicts, VariantPatchConflict } from "./findVariantPatchConflicts";
import { VariantInformation } from "./isVariantInformation";
import { isVariantPatch } from "./isVariantPatch";

/**
 * The composed ARS master or the conflicts or patch errors preventing composition.
 */
export type ApplyVariantPatchesResult<TMaster extends TlfArsTableReadDto | TlfArsListingReadDto> =
  | { success: true; data: TMaster }
  | { success: false; conflict: VariantPatchConflict }
  | { success: false; errors: NonNullable<ReturnType<typeof applyPatch>[number]>[] };

/**
 * Apply selected named patches to a clone of the master after checking for path conflicts.
 *
 * @param master - The master ARS item used as the patch baseline
 * @param variantInformation - Persisted patch catalogue
 * @param patchNames - Patch names to apply in catalogue order
 * @returns The composed ARS item, or the first conflict preventing composition
 */
export const applyVariantPatches = <TMaster extends TlfArsTableReadDto | TlfArsListingReadDto>(
  master: TMaster,
  variantInformation: VariantInformation,
  patchNames: string[],
): ApplyVariantPatchesResult<TMaster> => {
  const conflicts = findVariantPatchConflicts(variantInformation, patchNames);
  if (conflicts.length > 0) return { success: false, conflict: conflicts[0] };

  const current = cloneDeep(master);
  const selectedNames = new Set(patchNames);
  const orderedPatchNames = Object.keys(variantInformation.allVariantPatches).filter((name) =>
    selectedNames.has(name),
  );

  for (const name of orderedPatchNames) {
    const patch: Patch = variantInformation.allVariantPatches[name];
    if (isVariantPatch(patch)) {
      const errors = applyPatch(current, patch).filter((error) => error !== null);
      if (errors.length > 0) return { success: false, errors };
    }
  }

  return { success: true, data: current };
};
